import React from 'react';
import { 
  Typography, 
  Stack, 
  Paper, 
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert
} from '@mui/material';
import { useFormContext } from 'react-hook-form';

const ReviewStep: React.FC = () => {
  const { getValues } = useFormContext();
  const values = getValues();

  // Debug: surface full form data on review page for copy/paste comparisons
  console.log('Lease form data (review step):', JSON.stringify(values, null, 2));

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US');
  };

  const formatAddress = (address?: { street?: string; city?: string; state?: string; zip?: string }) => {
    if (!address) return '';
    const parts = [address.street, address.city, address.state, address.zip].filter(Boolean);
    return parts.join(', ');
  };

  const normalizedOtherDeposits: Array<{ description: string; amount: number }> = [];
  if (Array.isArray(values.deposits?.otherDeposits)) {
    values.deposits.otherDeposits.forEach((item: any) => {
      const amount = Number(item?.amount) || 0;
      const description = (item?.description || '').trim();
      if (amount > 0 && description) {
        normalizedOtherDeposits.push({ description, amount });
      }
    });
  }

  const otherDepositsTotal = normalizedOtherDeposits.reduce(
    (sum: number, item) => sum + (Number(item?.amount) || 0),
    0
  );
  const totalDeposits =
    (values.deposits?.security || 0) +
    (values.policies?.pets?.deposit || 0) +
    otherDepositsTotal;
  const moveInRent =
    (values.rent?.prorated && values.rent.prorated > 0
      ? values.rent.prorated
      : values.rent?.monthlyAmount) || 0;
  const totalMoveIn = moveInRent + totalDeposits;
  const noticeAddress =
    formatAddress(values.tenantNoticeAddress) ||
    formatAddress({
      street: values.property?.address?.street,
      city: values.property?.address?.city,
      state: values.property?.address?.state,
      zip: values.property?.address?.zip,
    });

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Review Lease Agreement
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review all information carefully before generating the lease agreement
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Please review all information carefully before generating the lease agreement.
      </Alert>
      
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Landlord Information
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">Name</Typography>
              <Typography variant="body1">{values.landlord?.fullName}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">Email</Typography>
              <Typography variant="body1">{values.landlord?.email}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary">Address</Typography>
              <Typography variant="body1">
                {values.landlord?.address?.street}, {values.landlord?.address?.city}, {values.landlord?.address?.state} {values.landlord?.address?.zip}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Pets
          </Typography>
          {values.policies?.pets?.allowed ? (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Allowed pet types: {(values.policies?.pets?.types || []).join(', ') || 'Not specified'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pet rent: {formatCurrency(values.policies?.pets?.rent || 0)} | Pet deposit: {formatCurrency(values.policies?.pets?.deposit || 0)}
              </Typography>
              {values.policies?.pets?.details?.length ? (
                <Stack spacing={1}>
                  {values.policies.pets.details.map((pet: any, idx: number) => (
                    <Typography key={idx} variant="body2">
                      {(pet.name || pet.type || `Pet ${idx + 1}`)} — {pet.type || 'Type N/A'}{pet.breed ? ` (${pet.breed})` : ''}{pet.weight ? `, ${pet.weight} lbs` : ''}{pet.age ? `, ${pet.age} yrs` : ''}{pet.description ? ` — ${pet.description}` : ''}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No detailed pet info added.
                </Typography>
              )}
              {values.policies?.pets?.notes ? (
                <Typography variant="body2" color="text.secondary">
                  Notes: {values.policies.pets.notes}
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Pets not allowed.
            </Typography>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Parking & Access
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2">Parking</Typography>
              <Typography variant="body2" color="text.secondary">
                {values.features?.parking?.included ? 'Parking included' : 'No parking included'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Spaces: {values.features?.parking?.spaces || 'N/A'} | Types: {(values.features?.parking?.types || []).join(', ') || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rules/Notes: {values.features?.parking?.rules || 'None specified'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">Access</Typography>
              <Typography variant="body2" color="text.secondary">
                Key sets provided: {values.features?.access?.keyCount || 0}
              </Typography>
              {values.features?.access?.keyTypes?.length ? (
                <Typography variant="body2" color="text.secondary">
                  Key types: {values.features.access.keyTypes.map((k: any) => `${k.type || 'Key'} (${k.count || 0})`).join(', ')}
                </Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                Garage remotes: {values.features?.access?.garageRemotes || 0} | Gate fobs: {values.features?.access?.gateFobs || 0}
              </Typography>
              {values.features?.access?.otherAccessDevices ? (
                <Typography variant="body2" color="text.secondary">
                  Other devices: {values.features.access.otherAccessDevices}
                </Typography>
              ) : null}
              {values.features?.access?.accessCodes ? (
                <Typography variant="body2" color="text.secondary">
                  Access codes: {values.features.access.accessCodes}
                </Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                Key replacement fee: {formatCurrency(values.features?.access?.keyReplacementFee || 0)}
              </Typography>
              {values.features?.access?.notes ? (
                <Typography variant="body2" color="text.secondary">
                  Notes: {values.features.access.notes}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Notices
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Tenant notice/mailing address: {noticeAddress || 'Uses premises address'}
            </Typography>
          </Stack>
        </Paper>
        
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Property Details
          </Typography>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary">Address</Typography>
              <Typography variant="body1">
                {values.property?.address?.street} {values.property?.address?.unit ? `Unit ${values.property.address.unit}` : ''}
                <br />
                {values.property?.address?.city}, {values.property?.address?.state} {values.property?.address?.zip}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">Type</Typography>
              <Typography variant="body1">{values.property?.type}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">Bedrooms</Typography>
              <Typography variant="body1">{values.property?.bedrooms}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">Bathrooms</Typography>
              <Typography variant="body1">{values.property?.bathrooms}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">Year Built</Typography>
              <Typography variant="body1">{values.property?.yearBuilt}</Typography>
            </Grid>
          </Grid>
        </Paper>
        
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Tenant(s)
          </Typography>
          <List>
            {values.tenants?.map((tenant: any, index: number) => (
              <ListItem key={index} divider>
                <ListItemText
                  primary={tenant.fullName}
                  secondary={`${tenant.email} | ${tenant.phone}`}
                />
                {index === 0 && <Chip label="Primary" size="small" color="primary" />}
              </ListItem>
            ))}
          </List>
        </Paper>
        
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Lease Terms
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="text.secondary">Type</Typography>
              <Typography variant="body1">
                {values.terms?.type === 'fixed' ? 'Fixed Term' : 'Month-to-Month'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="text.secondary">Start Date</Typography>
              <Typography variant="body1">
                {formatDate(values.terms?.startDate)}
              </Typography>
            </Grid>
            {values.terms?.type === 'fixed' && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body2" color="text.secondary">End Date</Typography>
                <Typography variant="body1">
                  {formatDate(values.terms?.endDate)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
        
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Financial Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="text.secondary">Monthly Rent</Typography>
              <Typography variant="h5" color="primary">
                {formatCurrency(values.rent?.monthlyAmount || 0)}
              </Typography>
            </Grid>
            {values.rent?.prorated ? (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body2" color="text.secondary">Prorated First Month</Typography>
                <Typography variant="h6">
                  {formatCurrency(values.rent.prorated || 0)}
                </Typography>
              </Grid>
            ) : null}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="text.secondary">Security Deposit</Typography>
              <Typography variant="h5">
                {formatCurrency(values.deposits?.security || 0)}
              </Typography>
            </Grid>
            {Boolean(values.policies?.pets?.deposit) && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body2" color="text.secondary">Pet Deposit</Typography>
                <Typography variant="h5">
                  {formatCurrency(values.policies?.pets?.deposit || 0)}
                </Typography>
              </Grid>
            )}
            {normalizedOtherDeposits.length ? (
              <Grid size={12}>
                <Typography variant="body2" color="text.secondary">Other Deposits</Typography>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {normalizedOtherDeposits.map((item, idx: number) => (
                    <Typography key={idx} variant="body2">
                      {item.description || 'Deposit'}: {formatCurrency(item.amount || 0)}
                    </Typography>
                  ))}
                </Stack>
              </Grid>
            ) : null}
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="text.secondary">Total Deposits</Typography>
              <Typography variant="h5">
                {formatCurrency(totalDeposits)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" color="text.secondary">Total Move-in</Typography>
              <Typography variant="h5" color="secondary">
                {formatCurrency(totalMoveIn)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Includes {formatCurrency(moveInRent)} first-month rent {values.rent?.prorated ? '(prorated)' : ''} plus all deposits
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Payment Information
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">Due Day</Typography>
              <Typography variant="body1">
                {values.rent?.dueDay} of each month {values.rent?.dueTime ? `(by ${values.rent.dueTime})` : ''}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">Late Fee</Typography>
              <Typography variant="body1">
                {values.rent?.lateFee?.type === 'fixed' 
                  ? formatCurrency(values.rent?.lateFee?.amount || 0)
                  : `${values.rent?.lateFee?.amount || 0}% of rent`
                } after {values.rent?.lateFee?.gracePeriod || 0} day grace period
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary">Accepted Payment Methods</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {values.rent?.paymentMethods?.map((method: string) => (
                  <Chip key={method} label={method} size="small" />
                ))}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Stack>
    </>
  );
};

export default ReviewStep; 
